import { supabase } from '@/lib/supabase';

export interface Resume {
  id: string;
  userId: string;
  name: string;
  targetRole?: string;
  status: 'draft' | 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
}

export interface ResumeVersion {
  id: string;
  resumeId: string;
  versionNumber: number;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  uploadDate: string;
  createdAt: string;
}

const toCamelCase = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(toCamelCase);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((result, key) => {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      result[camelKey] = toCamelCase(obj[key]);
      return result;
    }, {} as any);
  }
  return obj;
};

const toSnakeCase = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(toSnakeCase);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((result, key) => {
      const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
      result[snakeKey] = toSnakeCase(obj[key]);
      return result;
    }, {} as any);
  }
  return obj;
};

export const resumeService = {
  async getResumes(): Promise<Resume[]> {
    const { data, error } = await supabase
      .from('resumes')
      .select('*')
      .order('updated_at', { ascending: false });

    if (error) throw error;
    return toCamelCase(data || []);
  },

  async getResumeById(id: string): Promise<Resume | null> {
    const { data, error } = await supabase
      .from('resumes')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async getActiveResume(): Promise<Resume | null> {
    const { data, error } = await supabase
      .from('resumes')
      .select('*')
      .eq('status', 'active')
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data ? toCamelCase(data) : null;
  },

  async createResume(resume: Partial<Resume>): Promise<Resume> {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw new Error('User not authenticated');

    const resumeData = toSnakeCase({
      ...resume,
      userId: userData.user.id,
    });

    const { data, error } = await supabase
      .from('resumes')
      .insert([resumeData])
      .select()
      .single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async updateResume(id: string, updates: Partial<Resume>): Promise<Resume> {
    const updateData = toSnakeCase(updates);

    const { data, error } = await supabase
      .from('resumes')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async deleteResume(id: string): Promise<void> {
    const { error } = await supabase
      .from('resumes')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async getResumeVersions(resumeId: string): Promise<ResumeVersion[]> {
    const { data, error } = await supabase
      .from('resume_versions')
      .select('*')
      .eq('resume_id', resumeId)
      .order('version_number', { ascending: false });

    if (error) throw error;
    return toCamelCase(data || []);
  },

  async getLatestVersion(resumeId: string): Promise<ResumeVersion | null> {
    const { data, error } = await supabase
      .from('resume_versions')
      .select('*')
      .eq('resume_id', resumeId)
      .order('version_number', { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data ? toCamelCase(data) : null;
  },

  async createResumeVersion(version: Partial<ResumeVersion>): Promise<ResumeVersion> {
    const { data: nextVersionData, error: versionError } = await supabase
      .rpc('get_next_version_number', { p_resume_id: version.resumeId });

    if (versionError) throw versionError;

    const versionData = toSnakeCase({
      ...version,
      versionNumber: nextVersionData,
    });

    const { data, error } = await supabase
      .from('resume_versions')
      .insert([versionData])
      .select()
      .single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async deleteResumeVersion(id: string): Promise<void> {
    const { error } = await supabase
      .from('resume_versions')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};